import type { NotificationItineraryView } from '@notifications/entities/notification';
import {
  NOTIFICATION_NEXT_PAGE_SIZE,
  navigateToNotificationItinerary,
  useMarkAllRead,
  useMarkRead,
  useNotificationDrawerStore,
  useNotificationsData,
} from '@notifications/entities/notification';
import { useSupportDrawerStore } from '@support/entities/support-ticket';
import { useNavigate } from '@tanstack/react-router';
import { CheckCheck } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { Button, EmptyState, Skeleton, SlidePanel, Text } from '@/shared/ui';
import css from './notification-drawer.module.scss';
import { NotificationRow } from './notification-row';

const ListSkeleton = () => (
  <div className={css.skeletons}>
    {Array.from({ length: 5 }, (_, i) => `skeleton-${i}`).map((id) => (
      <div className={css.skeletonRow} key={id}>
        <Skeleton height="8px" variant="circular" width="8px" />
        <Skeleton width="45%" />
        <Skeleton width="64px" />
      </div>
    ))}
  </div>
);

// Mounted once at the AppLayout root so it overlays every page, shared by the
// desktop and mobile bells.
export const NotificationDrawer = () => {
  const open = useNotificationDrawerStore((s) => s.open);
  const closeDrawer = useNotificationDrawerStore((s) => s.closeDrawer);
  const expandedId = useNotificationDrawerStore((s) => s.expandedId);
  const toggleExpanded = useNotificationDrawerStore((s) => s.toggleExpanded);

  const {
    notifications: rows,
    unreadCount: unread,
    isLoading,
    isError,
    refetch,
    isRefetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useNotificationsData();
  // `mutate`, not the mutation object: `useMutation` returns a fresh object on
  // every render, so depending on it would give `handleToggle` a new identity
  // each poll and re-render every memoised row. `mutate` is `useCallback`'d on
  // the observer and is stable.
  const { mutate: markRead } = useMarkRead();
  const markAllRead = useMarkAllRead();
  const navigate = useNavigate();

  // Opening the drawer must never show a list that is up to a poll old. One
  // refetch on open costs a single request and removes the whole class of
  // "I opened it and my notification wasn't there yet".
  //
  // The ref is load-bearing: `refetch` is a fresh identity whenever the
  // provider re-renders, so depending on it directly would re-run this effect
  // on every poll — a refetch loop, not a refresh.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  useEffect(() => {
    if (open) refetchRef.current();
  }, [open]);

  // Stable across renders so the memoised cards are not invalidated every poll.
  //
  // `expandedId` is read from the store at click time rather than closed over:
  // depending on it would give this callback a new identity on every
  // expand/collapse, re-rendering EVERY card when only two actually changed.
  // `getState()` returns the pre-toggle value, which is exactly what the
  // is-this-opening test needs.
  const handleToggle = useCallback(
    (id: string) => {
      const wasExpanded = useNotificationDrawerStore.getState().expandedId;
      toggleExpanded(id);
      // Expanding is the moment the user actually reads it — the same
      // convention GitHub and Slack use. Only fires on open, and only when it
      // is still unread, so the optimistic count can never double-decrement.
      const target = rows.find((row) => row.id === id);
      if (wasExpanded !== id && target?.readOn === null) {
        markRead({ id });
      }
    },
    [markRead, rows, toggleExpanded]
  );

  const handleOpenItinerary = useCallback(
    (itineraryId: string, view: NotificationItineraryView) => {
      closeDrawer();
      navigateToNotificationItinerary(navigate, itineraryId, view);
    },
    [closeDrawer, navigate]
  );

  // A support ticket has no page of its own — it lives in the support drawer's
  // "My tickets" tab. So "View details" hands off between the two overlays
  // rather than navigating: this one closes, that one opens on the ticket.
  const handleOpenSupportTicket = useCallback(
    (ticketId: string) => {
      closeDrawer();
      useSupportDrawerStore.getState().openDrawerWithTicket(ticketId);
    },
    [closeDrawer]
  );

  const body = () => {
    if (isLoading) return <ListSkeleton />;
    if (isError) {
      return (
        <div className={css.state}>
          <Text variant="muted">
            Notifications couldn’t be loaded. Please try again.
          </Text>
          <Button
            disabled={isRefetching}
            onClick={() => refetch()}
            size="sm"
            variant="outline"
          >
            Retry
          </Button>
        </div>
      );
    }
    if (rows.length === 0) {
      // The shared primitive the Support drawer also uses, so the two empty
      // states are the same object rather than two lookalikes.
      return (
        <div className={css.empty}>
          <EmptyState
            description="No new notifications."
            icon={<CheckCheck style={{ height: 22, width: 22 }} />}
            title="You’re all caught up"
          />
        </div>
      );
    }
    return (
      <>
        <ul className={css.list}>
          {rows.map((notification) => (
            <NotificationRow
              expanded={expandedId === notification.id}
              key={notification.id}
              notification={notification}
              onOpenItinerary={handleOpenItinerary}
              onOpenSupportTicket={handleOpenSupportTicket}
              onToggle={handleToggle}
            />
          ))}
        </ul>
        {hasNextPage ? (
          <div className={css.more}>
            <Button
              disabled={isFetchingNextPage}
              onClick={() => fetchNextPage()}
              size="sm"
              variant="outline"
            >
              {isFetchingNextPage
                ? 'Loading…'
                : `Show ${NOTIFICATION_NEXT_PAGE_SIZE} more`}
            </Button>
          </div>
        ) : null}
      </>
    );
  };

  return (
    <SlidePanel
      onClose={closeDrawer}
      open={open}
      title="Notifications"
      width={460}
    >
      <div className={css.body}>
        <div className={css.toolbar}>
          <Text size="sm" variant="muted">
            {unread > 0 ? `${unread} unread` : 'All caught up'}
          </Text>
          <Button
            disabled={unread === 0 || markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
            size="sm"
            variant="ghost"
          >
            Mark all read
          </Button>
        </div>
        {body()}
      </div>
    </SlidePanel>
  );
};
